import {
  DiscountApplicationMethodEnum,
  IQuoteSettingsConfigSchema,
} from '@monetize/types/app';
import { useEffect } from 'react';
import {
  MAccordion,
  MAccordionCustomButton,
  MBox,
  MText,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { DISCOUNT_APPLICATION_METHOD_OPTIONS } from '../../../../../constants/discounts';
import { useAccordionControl } from '../../../../../hooks';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import { SettingsControl } from '../../../components/SettingsControl';
import { SettingsSection } from '../../../components/SettingsSection';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
} from '../quoteSettingsConfig';

interface QuoteDiscountSettingsProps {
  quoteSettings: IQuoteSettingsConfigSchema;
  onSubmit: (data: Partial<IQuoteSettingsConfigSchema>) => Promise<void>;
  isLoading: boolean;
}

export const QuoteDiscountSettings = ({
  quoteSettings,
  onSubmit,
  isLoading,
}: QuoteDiscountSettingsProps) => {
  const highlights = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.discountSettings),
  );
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useScrollToHighlightedSection(highlights, !isLoading);

  const shouldOpenAccordion = Object.values(highlights).some(Boolean);
  useEffect(() => {
    if (shouldOpenAccordion) {
      setAccordionValue(['0']);
    }
  }, [shouldOpenAccordion, setAccordionValue]);

  return (
    <MAccordion
      multiple
      data-testid="quote-discount-settings"
      w="100%"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MSettingAccordionItem
        value="0"
        id={QUOTE_SETTING_KEYS.discountSettings}
        {...getHighlightStyles(highlights[QUOTE_SETTING_KEYS.discountSettings])}
        renderAccordionButton={({ isExpanded }: { isExpanded: boolean }) => (
          <MAccordionCustomButton
            isExpanded={isExpanded}
            label="Discount Settings"
          />
        )}
        borderWidth="1px"
      >
        <MBox mt="4">
          <SettingsSection
            id={QUOTE_SETTING_KEYS.discountApplicationMethod}
            {...getHighlightStyles(
              highlights[QUOTE_SETTING_KEYS.discountApplicationMethod],
            )}
            showDivider={false}
          >
            <SettingsControl
              name="discountApplicationMethod"
              heading="Discount Application Method"
              subheading={
                <MText>
                  When 'Combined' is selected, catalog discounts and custom
                  discounts are summed and applied together. When 'Sequential'
                  is selected, the custom discount is applied first, then the
                  catalog discount is applied to the resulting price.
                </MText>
              }
              fetchLoading={isLoading}
              isDisabled={false}
              control={
                <MCustomSelect
                  name="discountApplicationMethod"
                  items={DISCOUNT_APPLICATION_METHOD_OPTIONS}
                  value={
                    quoteSettings.discountApplicationMethod ??
                    DiscountApplicationMethodEnum.COMBINED
                  }
                  onChange={(newValue: DiscountApplicationMethodEnum) =>
                    onSubmit({ discountApplicationMethod: newValue })
                  }
                  width="200px"
                />
              }
            />
          </SettingsSection>
        </MBox>
      </MSettingAccordionItem>
    </MAccordion>
  );
};
